/**
 * AppStatus — status pill for a deployed managed-app instance.
 *
 * Sibling of `PipelineStatus` (used by Pipelines). Apps and Pipelines
 * look superficially alike but their lifecycle semantics differ:
 *
 *   Pipelines (workflows) sit ready/sleeping awaiting trigger — "Live"
 *     requires a warm-pool subscription, otherwise the bundle is
 *     "Sleeping" by design.
 *
 *   Apps are persistent processes (n8n, grafana, …). A running app is
 *     RUNNING, full stop — calling that "Sleeping" because there's no
 *     warm pool is nonsensical and visually misleading.
 *
 * Reuses the same `.pipelines-status-*` colour classes from
 * PipelinesPage.css (no new colours) but maps app-side raw `status`
 * directly onto the visual key + label:
 *
 *   row.status                     → pill state + colour
 *   ─────────────────────────────────────────────────────────────
 *   running / ready / active       → Running    (green pulse)
 *   pending / starting / provisioning → Building (amber pulse)
 *   upgrading                      → Upgrading  (amber pulse) *
 *   failed / error                 → Failed     (red)
 *   stopped / destroyed            → Stopped    (gray)
 *   anything else / null           → Unknown    (gray)
 *
 * * upgrading is overridden to 'Running' when running >= desired >= 1
 *   — during a rolling deploy ECS keeps the old task alive until the
 *   new one is healthy, so seeing "Stopped/Upgrading" while the app
 *   IS actually serving traffic is misleading. The DDB row's status
 *   field stays 'upgrading' until manually cleared, so we have to
 *   derive the truthful pill from live ECS state here.
 */

import React from 'react';

const KEY_FROM_STATUS = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'running' || s === 'ready' || s === 'active') return 'live';
  if (s === 'pending' || s === 'starting' || s === 'provisioning') return 'building';
  if (s === 'upgrading') return 'upgrading';
  if (s === 'failed' || s === 'error') return 'failed';
  if (s === 'stopped' || s === 'destroyed') return 'sleeping';
  return 'sleeping';
};

const LABEL_BY_KEY = {
  live:      'Running',
  building:  'Building',
  upgrading: 'Upgrading',
  failed:    'Failed',
  sleeping:  'Stopped',
};

const AppStatus = ({ status, runningCount, desiredCount, variant = 'table' }) => {
  let key = KEY_FROM_STATUS(status);
  // Rolling-deploy reality override — see header comment. Once ECS
  // reports a healthy task count, the pill goes green even if the
  // DDB row hasn't been flipped back from 'upgrading' yet.
  if (key === 'upgrading' && Number(runningCount) >= 1 && Number(runningCount) >= Number(desiredCount || 0)) {
    key = 'live';
  }
  const label = LABEL_BY_KEY[key];
  // Reuse the Pipelines stylesheet's colour classes so we don't add
  // any new colour palette. `variant` picks the size/typography
  // surface (table cell vs detail-page header), matching PipelineStatus.
  // The 'upgrading' key piggybacks on the 'building' colour class
  // because no Pipelines class exists for it — both want amber pulse.
  const prefix = variant === 'header' ? 'pipeline-detail-status' : 'pipelines-status';
  const colourKey = key === 'upgrading' ? 'building' : key;
  return (
    <span className={`${prefix} ${prefix}-${colourKey}`}>
      <span className={`${prefix}-dot`} />
      {label}
    </span>
  );
};

export default AppStatus;
